<template>
  <section class="claim-wrap" ref="wrapRef" aria-label="Propuesta de valor">
    <h1 class="claim" aria-live="polite">
      <span class="brand">Latin Dreamer</span> es
      <!-- Rotador -->
      <span class="rotator" ref="rotatorRef">
        <span class="rotator__bg" ref="bgRef"></span>
        <span class="rotator__word" ref="wordRef">calidad</span>
      </span>
      para tu proyecto de negocios en línea.
    </h1>

  <p class="claim-sub">
  Ayudamos a negocios como el tuyo en Estados Unidos a vender más con páginas web, sistemas y soluciones hechas a tu medida.
</p>

  </section>
</template>

<script>
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'HeadlineRotatorio',
  props: {
    // Puedes pasar tus propias palabras si quieres
    words: {
      type: Array,
      default: () => ['calidad', 'experiencia', 'confianza']
    },
    // Color del bloque
    accent: { type: String, default: '#5b22a6' }
  },
  setup(props) {
    const wrapRef = ref(null)
    const rotatorRef = ref(null)
    const wordRef = ref(null)
    const bgRef = ref(null)

    let tl // timeline del rotador
    let st // scrollTrigger para pausar/reanudar

    onMounted(() => {
      // Estilos dinámicos del fondo
      if (bgRef.value) bgRef.value.style.setProperty('--accent', props.accent)

      // Estados iniciales
      gsap.set(wordRef.value, { yPercent: 0, opacity: 1 })
      gsap.set(bgRef.value, { scaleX: 0, transformOrigin: 'left center' })

      // Construimos la animación de rotación de palabras
      const cycle = (words) => {
        const localTl = gsap.timeline({ repeat: -1, defaults: { ease: 'power2.out' } })
        const showDur = 1.2   // tiempo visible por palabra
        const transDur = 0.35 // transición in/out

        words.forEach((w, i) => {
          // Cambiar texto justo antes de entrar
          localTl.add(() => { wordRef.value.textContent = w }, 'label' + i)

          // Entrada: estiramos fondo y metemos la palabra desde abajo
          localTl.to(bgRef.value, { scaleX: 1, duration: transDur }, 'label' + i)
                 .fromTo(
                   wordRef.value,
                   { yPercent: 100, opacity: 0 },
                   { yPercent: 0, opacity: 1, duration: transDur },
                   'label' + i
                 )
                 // Pequeño "pop" del fondo
                 .to(bgRef.value, { scaleX: 1.04, duration: 0.12, ease: 'power1.inOut' })
                 .to(bgRef.value, { scaleX: 1, duration: 0.14, ease: 'power1.out' })

                 // Mostrar un rato
                 .to({}, { duration: showDur })

                 // Salida: escondemos palabra hacia arriba y retraemos el fondo
                 .to(wordRef.value, { yPercent: -100, opacity: 0, duration: transDur, ease: 'power2.in' })
                 .to(bgRef.value, { scaleX: 0, duration: transDur }, '<')
        })
        return localTl
      }

      tl = cycle(props.words)
      tl.pause() // arrancará sólo cuando entre al viewport

      // Disparar/pausar por viewport
      st = ScrollTrigger.create({
        trigger: wrapRef.value,
        start: 'top 80%',
        onEnter: () => tl.play(),
        onLeave: () => tl.pause(),
        onEnterBack: () => tl.play(),
        onLeaveBack: () => tl.pause()
      })

      // Animación inicial del título completo (suave)
      gsap.from(wrapRef.value.querySelector('.claim'), {
        opacity: 0,
        y: 20,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: { trigger: wrapRef.value, start: 'top 85%', once: true }
      })
      gsap.from('.claim-sub', {
        opacity: 0,
        y: 14,
        duration: 0.5,
        ease: 'power2.out',
        scrollTrigger: { trigger: wrapRef.value, start: 'top 80%', once: true, delay: 0.1 }
      })
    })

    onBeforeUnmount(() => {
      tl && tl.kill()
      st && st.kill()
      ScrollTrigger.getAll().forEach(t => t.kill())
    })

    return { wrapRef, rotatorRef, wordRef, bgRef }
  }
}
</script>

<style scoped>
/* Contenedor */
.claim-wrap{
  max-width: 1200px;
  margin: 64px auto 42px;
  padding: 0 16px;
  text-align: center;
}

/* Título principal */
.claim{
  margin: 0 auto 12px;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif;
  font-weight: 700;
  line-height: 1.15;
  color: #111827;
  font-size: 48px;         /* desktop base */
}

/* “Epika” */
.brand{ font-weight: 800; }

/* Rotador (bloque morado) */
.rotator{
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 .14em;
  margin: 0 .18em;
  min-width: 6ch;          /* evita que salte el layout entre palabras */
  height: 1.15em;
  vertical-align: baseline;
  isolation: isolate;
}
.rotator__bg{
  --accent: #5b22a6;
  position: absolute;
  inset: 0;
  background: var(--accent);
  border-radius: 6px;
  transform: scaleX(0);
}
.rotator__word{
  position: relative;
  z-index: 1;
  color: #fff;
  font-weight: 800;
  letter-spacing: .2px;
  display: inline-block;
  white-space: nowrap;
}

/* Subtítulo */
.claim-sub{
  margin: 10px auto 0;
  max-width: 1000px;
  color: #6b7280;
  font-size: 22px;
  line-height: 1.55;
}

/* Responsivo */
@media (max-width: 1024px){
  .claim{ font-size: 42px; }
  .claim-sub{ font-size: 20px; }
}
@media (max-width: 768px){
  .claim{ font-size: 34px; line-height: 1.2; }
  .rotator{ min-width: 5.6ch; }
  .claim-sub{ font-size: 18px; }
}
@media (max-width: 480px){
  .claim{ font-size: 28px; line-height: 1.25; }
  .claim-wrap{ margin: 48px auto 34px; }
  .claim-sub{ font-size: 16px; }
}
</style>
